import type { Task } from '../api';

/**
 * Where a task had got to, and what the next step on it is.
 *
 * Two personas found this independently and reported the same consequence. *Sort out the tax
 * return* is 2 of 10 ticked with the next step *"Reset the password because it locked"*, and the
 * row read `Sort out the tax return · 31d late · 45m`. The reader sees 45m, decides they have not
 * got 45 minutes, and closes the tab — while the thing actually in front of them is a two-minute
 * password reset. Every Today shortlist row's text was dumped in that round and not one contained
 * a fraction.
 *
 * A second line in `task-main`, deliberately not a pill in `.task-meta`. The meta strip's drop
 * order governs what falls off first at narrow widths, and this is the one thing on the row that
 * must not: it is the reason to start. Below 40rem the strip is a line of its own anyway, so a
 * pill there would have cost the same height and been the first thing dropped.
 *
 * **The fraction never truncates and the step does.** `2/10` is four characters and is what says
 * "you are part-way in"; the step's text is what says where, and half a step title still reads as
 * a step. The other way round gives you an ellipsis where the number was.
 *
 * Nothing at all with no checklist, which is the rule `SubtaskPill` states about itself: a row
 * never reads `0/0`. And the glyph plus the `title` are the architect's half of the finding — the
 * badge that *did* appear was ambiguous about whether it counted steps or subtasks, so both now
 * say which they are.
 */
export function ChecklistLine({ task }: { task: Pick<Task, 'checklist'> }) {
  const { done, total, next } = task.checklist;
  if (total === 0) return null;

  return (
    <span className="checklist-line" data-testid="checklist-line">
      <span
        className="checklist-count"
        /*
          Spelled out rather than left to the glyph. "2/10" beside a tick could be subtasks, which
          is exactly the confusion reported — a task showing `0/3` in the full view and nothing on
          the row, next to tasks showing `0/2` and `0/3` for subtasks.
        */
        title={`${done} of ${total} checklist steps done`}
      >
        {/* The glyph is decorative: the `title` and the fraction carry the meaning (SC 1.4.1). */}
        <span aria-hidden="true">☑ </span>
        {done}/{total}
      </span>
      {next !== null && (
        <span className="checklist-next" title={`Next step: ${next}`}>
          next: {next}
        </span>
      )}
    </span>
  );
}
